import { useStore } from '../state/store';
import { BankArea, RecordTable } from './BankArea';
import { Badge, formatMoney } from '../design/primitives';

export function Payments() {
  const { bootstrap } = useStore();
  if (!bootstrap) return null;
  const { accounts, transactions, cards, customers } = bootstrap.records;

  const nameOf = (customerId: string) =>
    customers.find((customer: any) => customer.id === customerId)?.name ?? customerId;

  return (
    <BankArea
      serverName="payments-operations-mcp"
      lede="Payments operations run through an agent with access to balances, transaction history, wire transfers, beneficiary details and card controls. Reads clear instantly; anything that moves money is held for a second person."
    >
      <RecordTable
        title="Accounts"
        columns={[
          { key: 'iban', label: 'IBAN' },
          { key: 'holder', label: 'Holder' },
          { key: 'product', label: 'Product' },
          { key: 'balance', label: 'Balance', align: 'right' },
          { key: 'available', label: 'Available', align: 'right' },
        ]}
        rows={accounts.map((account: any) => ({
          iban: <code className="mb-mono">{account.iban}</code>,
          holder: nameOf(account.customerId),
          product: account.product,
          balance: <span className="mb-numeric">{formatMoney(account.balance, account.currency)}</span>,
          available: <span className="mb-numeric">{formatMoney(account.available, account.currency)}</span>,
        }))}
      />

      <RecordTable
        title="Recent transactions"
        columns={[
          { key: 'date', label: 'Booked' },
          { key: 'counterparty', label: 'Counterparty' },
          { key: 'reference', label: 'Reference' },
          { key: 'amount', label: 'Amount', align: 'right' },
        ]}
        rows={transactions.slice(0, 10).map((transaction: any) => ({
          date: transaction.bookedAt.slice(0, 10),
          counterparty: transaction.counterparty,
          reference: transaction.reference,
          amount: (
            <span
              className="mb-numeric"
              style={{ color: transaction.direction === 'debit' ? 'var(--c-block)' : 'var(--c-allow)' }}
            >
              {transaction.direction === 'debit' ? '\u2212' : '+'}
              {formatMoney(transaction.amount, transaction.currency)}
            </span>
          ),
        }))}
      />

      <RecordTable
        title="Cards"
        columns={[
          { key: 'masked', label: 'Card' },
          { key: 'holder', label: 'Holder' },
          { key: 'network', label: 'Network' },
          { key: 'status', label: 'Status' },
        ]}
        rows={cards.map((card: any) => ({
          masked: <code className="mb-mono">{card.masked}</code>,
          holder: nameOf(card.customerId),
          network: card.network,
          status: <Badge tone={card.status === 'active' ? 'allow' : 'approval'}>{card.status}</Badge>,
        }))}
      />
    </BankArea>
  );
}
